import { FormEvent, useEffect, useState } from "react";
import { AtSign, Eye, EyeOff, FileText, Hash, Lock, Mail, Phone, User } from "lucide-react";
import api from "../../api";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage, getFieldErrors } from "../../utils/apiError";
import { ApiResponse, Customer } from "../../types";
import Modal from "../../components/ui/Modal";
import Button from "../../components/ui/Button";
import { Checkbox, Input } from "../../components/ui/FormField";

interface Props {
    open: boolean;
    customer: Customer | null; // null = create
    onClose: () => void;
    onSaved: (customer: Customer) => void;
}

interface FormState {
    name: string;
    legalName: string;
    crNo: string;
    trn: string;
    trnNotApplicable: boolean;
    contactPerson: string;
    mobile1: string;
    mobile2: string;
    email: string;
    hasLogin: boolean;
    username: string;
    password: string;
}

const EMPTY: FormState = {
    name: "",
    legalName: "",
    crNo: "",
    trn: "",
    trnNotApplicable: false,
    contactPerson: "",
    mobile1: "",
    mobile2: "",
    email: "",
    hasLogin: false,
    username: "",
    password: "",
};

export default function CustomerFormModal({ open, customer, onClose, onSaved }: Props) {
    const toast = useToast();
    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const isEdit = !!customer;

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setShowPassword(false);
        setForm(
            customer
                ? {
                      ...EMPTY,
                      name: customer.name,
                      legalName: customer.legalName,
                      crNo: customer.crNo,
                      trn: customer.trn ?? "",
                      trnNotApplicable: customer.trnNotApplicable,
                      contactPerson: customer.contactPerson ?? "",
                      mobile1: customer.mobile1,
                      mobile2: customer.mobile2 ?? "",
                      email: customer.email ?? "",
                  }
                : EMPTY
        );
    }, [open, customer]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: "" }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const body: Record<string, unknown> = {
            name: form.name.trim(),
            legalName: form.legalName.trim(),
            crNo: form.crNo.trim(),
            trnNotApplicable: form.trnNotApplicable,
            trn: form.trnNotApplicable ? null : form.trn.trim(),
            contactPerson: form.contactPerson.trim(),
            mobile1: form.mobile1.trim(),
            mobile2: form.mobile2.trim() || null,
            email: form.email.trim() || null,
        };

        if (!isEdit) {
            body.hasLogin = form.hasLogin;
            if (form.hasLogin) {
                body.username = form.username.trim();
                body.password = form.password;
            }
        }

        setSaving(true);
        try {
            const res = isEdit
                ? await api.put<ApiResponse<Customer>>(`/customers/${customer!.id}`, body)
                : await api.post<ApiResponse<Customer>>("/customers", body);

            toast.success(isEdit ? "Customer updated" : "Customer created");
            onSaved(res.data.data);
        } catch (err) {
            setErrors(getFieldErrors(err));
            toast.error(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            locked={saving}
            size="lg"
            title={isEdit ? `Edit ${customer?.name}` : "Add customer"}
            subtitle={isEdit ? `Customer #${customer?.customerId}` : "Customer ID is assigned automatically."}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="submit" form="customer-form" loading={saving}>
                        {isEdit ? "Save changes" : "Create customer"}
                    </Button>
                </>
            }
        >
            <form id="customer-form" onSubmit={handleSubmit} className="space-y-6">
                <Section title="Business">
                    <Input label="Name" required autoFocus icon={User} value={form.name} onChange={(e) => set("name", e.target.value)} error={errors.name} placeholder="Trade name" />
                    <Input label="Legal name" required icon={FileText} value={form.legalName} onChange={(e) => set("legalName", e.target.value)} error={errors.legalName} placeholder="As on CR" />
                    <Input label="CR number" required icon={Hash} value={form.crNo} onChange={(e) => set("crNo", e.target.value)} error={errors.crNo} />
                    <div className="space-y-2">
                        <Input
                            label="TRN (VAT number)"
                            required={!form.trnNotApplicable}
                            icon={Hash}
                            value={form.trnNotApplicable ? "" : form.trn}
                            disabled={form.trnNotApplicable}
                            onChange={(e) => set("trn", e.target.value)}
                            error={errors.trn}
                            placeholder={form.trnNotApplicable ? "Not applicable" : "15-digit VAT number"}
                        />
                        <label className="flex items-center gap-2 text-xs text-slate-400 ml-1 cursor-pointer w-fit">
                            <input
                                type="checkbox"
                                checked={form.trnNotApplicable}
                                onChange={(e) => set("trnNotApplicable", e.target.checked)}
                                className="h-3.5 w-3.5 accent-[#8b5cf6]"
                            />
                            TRN not applicable
                        </label>
                    </div>
                </Section>

                <Section title="Contact">
                    <Input label="Contact person" required icon={User} value={form.contactPerson} onChange={(e) => set("contactPerson", e.target.value)} error={errors.contactPerson} />
                    <Input label="Email" type="email" required={form.hasLogin} icon={Mail} value={form.email} onChange={(e) => set("email", e.target.value)} error={errors.email} />
                    <Input label="Mobile 1" required type="tel" icon={Phone} value={form.mobile1} onChange={(e) => set("mobile1", e.target.value)} error={errors.mobile1} placeholder="+966…" />
                    <Input label="Mobile 2" type="tel" icon={Phone} value={form.mobile2} onChange={(e) => set("mobile2", e.target.value)} error={errors.mobile2} />
                </Section>

                {!isEdit && (
                    <div className="space-y-4">
                        <Checkbox
                            label="Create a login for this customer"
                            description="Lets the customer sign in to the portal. Requires an email."
                            checked={form.hasLogin}
                            onChange={(v) => set("hasLogin", v)}
                        />
                        {form.hasLogin && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input
                                    label="Username"
                                    required
                                    icon={AtSign}
                                    autoComplete="off"
                                    value={form.username}
                                    onChange={(e) => set("username", e.target.value)}
                                    error={errors.username}
                                />
                                <Input
                                    label="Password"
                                    required
                                    minLength={8}
                                    icon={Lock}
                                    autoComplete="new-password"
                                    type={showPassword ? "text" : "password"}
                                    value={form.password}
                                    onChange={(e) => set("password", e.target.value)}
                                    error={errors.password}
                                    hint="At least 8 characters"
                                    rightSlot={
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((s) => !s)}
                                            className="p-1 text-slate-500 hover:text-white"
                                            aria-label="Toggle password"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    }
                                />
                            </div>
                        )}
                    </div>
                )}
            </form>
        </Modal>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <p className="text-xs font-bold text-[#a78bfa] uppercase tracking-[0.2em] mb-3">{title}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}